import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, MenuOpen } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ADMIN_NAV_ITEMS,
  ADMIN_SIDEBAR_COLLAPSED_WIDTH,
  ADMIN_SIDEBAR_EXPANDED_WIDTH,
  ADMIN_SIDEBAR_STORAGE_KEY,
} from '../config/adminNavItems';

const GREEN = '#2e7d32';

const AdminQuickAccessSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(ADMIN_SIDEBAR_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(ADMIN_SIDEBAR_STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  const width = collapsed ? ADMIN_SIDEBAR_COLLAPSED_WIDTH : ADMIN_SIDEBAR_EXPANDED_WIDTH;

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          height: '100vh',
          maxHeight: '100vh',
          overflowX: 'hidden',
          overflowY: 'auto',
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        },
      }}
      open
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 48,
          px: collapsed ? 0.5 : 1.5,
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 0.5,
        }}
      >
        {!collapsed && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
            }}
          >
            Command Center
          </Typography>
        )}
        <Tooltip title={collapsed ? 'Expand menu' : 'Collapse menu'} placement="right">
          <IconButton
            size="small"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand Command Center menu' : 'Collapse Command Center menu'}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      </Toolbar>
      <Divider />
      <List dense sx={{ py: 1, px: collapsed ? 0.5 : 1 }}>
        {ADMIN_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const button = (
            <ListItemButton
              key={item.path}
              selected={active}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 1.5,
                mb: 0.25,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : 1.5,
                '&.Mui-selected': {
                  bgcolor: 'rgba(46, 125, 50, 0.12)',
                  color: GREEN,
                  '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.18)' },
                  '& .MuiListItemIcon-root': { color: GREEN },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 40,
                  justifyContent: 'center',
                  color: active ? GREEN : 'text.secondary',
                }}
              >
                <Icon fontSize="small" />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    noWrap: true,
                  }}
                />
              )}
            </ListItemButton>
          );

          return collapsed ? (
            <Tooltip key={item.path} title={item.label} placement="right">
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </List>
      {collapsed && (
        <Box sx={{ mt: 'auto', pb: 1, display: 'flex', justifyContent: 'center' }}>
          <Tooltip title="Expand menu" placement="right">
            <IconButton size="small" onClick={() => setCollapsed(false)} aria-label="Expand Command Center menu">
              <MenuOpen fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Drawer>
  );
};

export default AdminQuickAccessSidebar;
